import type { ResponseUser } from '../types'
import {
  responseRecovery,
  type createResponseRecovery,
} from '../lib/response-recovery'
import { ResponseContractError, type ResponseRequestContext } from './response-contract-error'
import {
  requestHeader,
  responseRequestContextFromHeaders,
} from './request-context'
import { requireRequestedProps, validatePageResponse } from './validate-response'
import { operationResponseKind } from './responses'

export type HttpRequestConfig = Readonly<{
  method: string
  url: string
  data?: unknown
  params?: Record<string, unknown>
  headers?: Record<string, unknown>
  signal?: AbortSignal
  onUploadProgress?: (event: unknown) => void
}>

export type HttpResponseData = string | Record<string, unknown>

export type HttpResponse = Readonly<{
  status: number
  data: HttpResponseData
  headers: Record<string, string>
}>

export type HttpClient = Readonly<{
  request(config: HttpRequestConfig): Promise<HttpResponse>
}>

type Recovery = ReturnType<typeof createResponseRecovery>

type ContractHttpClientDependencies = Readonly<{
  recovery?: Recovery
  report?: (error: ResponseContractError, context: ResponseRequestContext, user: ResponseUser | null) => void
}>

function responseHeader(response: HttpResponse, name: string): string | undefined {
  const entry = Object.entries(response.headers).find(([key]) => key.toLowerCase() === name.toLowerCase())
  return entry?.[1]
}

function transportError(path: string, code: string, message: string): ResponseContractError {
  return new ResponseContractError({
    contract: 'inertia.transport',
    issues: [{ path, code, message }],
  })
}

function parseRequestedProps(config: HttpRequestConfig): string[] {
  return (requestHeader(config.headers, 'X-Inertia-Partial-Data') ?? '')
    .split(',')
    .map((prop) => prop.trim())
    .filter(Boolean)
}

export class ContractHttpClient implements HttpClient {
  private readonly recovery: Recovery
  private readonly report: NonNullable<ContractHttpClientDependencies['report']>
  private lastValidUser: ResponseUser | null = null
  private lastValidPage: ReturnType<typeof validatePageResponse>['page'] | undefined

  constructor(
    private readonly inner: HttpClient,
    dependencies: ContractHttpClientDependencies = {},
  ) {
    this.recovery = dependencies.recovery ?? responseRecovery
    this.report = dependencies.report ?? (() => {})
  }

  async request(config: HttpRequestConfig): Promise<HttpResponse> {
    let response: HttpResponse
    try {
      response = await this.inner.request(config)
    } catch (error) {
      const rejectedResponse = this.rejectedResponse(error)
      if (!rejectedResponse) throw error
      if (this.isInertiaRedirect(rejectedResponse)) throw error
      response = rejectedResponse
    }
    const baseContext = responseRequestContextFromHeaders(config.headers, config.url)
    const context = { ...baseContext, status: response.status }

    try {
      if (response.status === 204) {
        if (operationResponseKind(context.operation) !== 'no-content') {
          throw transportError('status', 'unexpected_no_content', 'No no-content response contract is registered for this operation')
        }
        this.recovery.clearAfterValidResponse()
        return response
      }
      this.validateTransport(response)
      const rawPage = this.parsePage(response)
      const requestedProps = parseRequestedProps(config)
      if (requestedProps.length > 0) requireRequestedProps(rawPage.props, requestedProps)

      const candidate = requestedProps.length > 0 && this.lastValidPage
        ? {
            ...this.lastValidPage,
            ...rawPage,
            props: { ...this.lastValidPage.props, ...rawPage.props },
            flash: rawPage.flash ?? {},
          }
        : rawPage
      const result = validatePageResponse(candidate, { operation: context.operation })
      const safeRawPage = requestedProps.length > 0
        ? {
            ...rawPage,
            props: Object.fromEntries(
              Object.keys(rawPage.props).map((name) => [name, result.page.props[name]]),
            ),
            flash: result.page.flash,
          }
        : result.page

      this.lastValidPage = result.page
      this.lastValidUser = result.page.props.user as ResponseUser | null
      if (result.contractError) {
        this.recovery.recordFlashFailures(result.flashFailures, context.action === 'explicit')
        this.report(result.contractError, {
          ...context,
          component: result.page.component,
        }, this.lastValidUser)
      } else {
        this.recovery.clearAfterValidResponse()
      }

      return { ...response, data: safeRawPage }
    } catch (error) {
      if (!(error instanceof ResponseContractError)) throw error
      this.recovery.recover(error, context)
      this.report(error, context, this.lastValidUser)
      throw error
    }
  }

  private rejectedResponse(error: unknown): HttpResponse | undefined {
    if (!error || typeof error !== 'object' || !Object.hasOwn(error, 'response')) return
    const response = (error as { response?: unknown }).response
    if (!response || typeof response !== 'object') return
    const candidate = response as Partial<HttpResponse>
    if (
      typeof candidate.status !== 'number'
      || typeof candidate.data !== 'string'
      || !candidate.headers
      || typeof candidate.headers !== 'object'
    ) return
    return candidate as HttpResponse
  }

  private isInertiaRedirect(response: HttpResponse): boolean {
    return response.status === 409 && (
      responseHeader(response, 'x-inertia-location') !== undefined
      || responseHeader(response, 'x-inertia-redirect') !== undefined
    )
  }

  seed(page: unknown): void {
    const result = validatePageResponse(page)
    this.lastValidPage = result.page
    this.lastValidUser = result.page.props.user as ResponseUser | null
  }

  private validateTransport(response: HttpResponse): void {
    if (response.status < 200 || response.status >= 300) {
      throw transportError('status', 'unexpected_status', `Unexpected response status ${response.status}`)
    }
    const contentType = responseHeader(response, 'content-type')
    if (!contentType?.toLowerCase().includes('application/json')) {
      throw transportError('headers.content-type', 'unexpected_content_type', 'Expected an application/json response')
    }
    if (responseHeader(response, 'x-inertia')?.toLowerCase() !== 'true') {
      throw transportError('headers.x-inertia', 'missing_inertia_marker', 'Expected an Inertia response')
    }
  }

  private parsePage(response: HttpResponse): Record<string, unknown> & { props: Record<string, unknown> } {
    let value: unknown
    if (typeof response.data === 'string') {
      try {
        value = JSON.parse(response.data)
      } catch {
        throw transportError('body', 'invalid_json', 'Expected a parseable JSON response')
      }
    } else {
      value = response.data
    }
    if (!value || typeof value !== 'object' || Array.isArray(value)) {
      throw transportError('body', 'invalid_page', 'Expected an Inertia page object')
    }
    const props = (value as Record<string, unknown>).props
    if (!props || typeof props !== 'object' || Array.isArray(props)) {
      throw transportError('body.props', 'invalid_props', 'Expected an Inertia props object')
    }
    return value as Record<string, unknown> & { props: Record<string, unknown> }
  }
}
